import { useState } from 'react';
import { Button, Grid, Typography, makeStyles, Theme } from '@material-ui/core';
import { orderBy } from 'lodash';
import { Link, useHistory } from 'react-router-dom';
import { Route } from 'react-router-dom';
import { FileCopy } from '@material-ui/icons';

import { WithRequest } from '../../utility/WithRequest';
import { PathBuilder, PathPattern } from '../../../routing';

import { AlarmHistoryLoader } from './AlarmHistoryLoader';

import { getAnalysisUrl } from '../../../state/contexts/alarmsPage';

import { AlarmDetails } from 'src/models';
import { dividerColor, primaryGreen } from 'src/data/colors';
import { addNote, acknowledgeAlarm, resolveAlarm } from '../../../state/entities';

import UpdateModal from './UpdateModal/UpdateModal';
import AlarmResolveModal from './UpdateModal/ResolveModal';
import { renderSuccess, renderError } from 'src/components/ui/Snackbar';

import FaultModal from './UpdateModal/FaultModal';

const useStyles = makeStyles<Theme, { multipleSections: boolean }>((theme: Theme) => ({
  section: {
    backgroundColor: 'white',
    border: `1px solid ${dividerColor}`,
    borderRadius: theme.spacing(1),
    height: '100%',
    padding: theme.spacing(2),
  },
  sectionContainer: ({ multipleSections }) => ({
    width: multipleSections ? '50%' : '100%',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  }),
  label: {
    color: '#000000DE',
    fontSize: '0.75rem',
    letterSpacing: '0.1em',
    marginBottom: theme.spacing(1),
    textTransform: 'uppercase',
  },
  errorCode: {
    fontSize: '1.5rem',
    fontWeight: 500,
    marginBottom: theme.spacing(1),
  },
  copyIcon: {
    color: '#000000DE',
    cursor: 'pointer',
    marginLeft: theme.spacing(2),
  },
  learnMore: {
    color: primaryGreen,
    backgroundColor: 'transparent',
    border: 'none',
    padding: 0,
  },
  analysisButton: {
    border: `1px solid ${primaryGreen}`,
    color: primaryGreen,
    marginTop: theme.spacing(2),
    padding: '8px 16px',
    width: '100%',
  },
  tabs: {
    marginBottom: theme.spacing(2),
    marginTop: 16,
  },
  tab: {
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: 500,
    marginRight: theme.spacing(3),
    paddingBottom: theme.spacing(2),
    '&.active': {
      color: primaryGreen,
      borderBottom: `2px solid ${primaryGreen}`,
    },
  },
  activity: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: theme.spacing(2),
    overflowY: 'scroll',
  },
  logItem: {
    alignItems: 'flex-start',
    borderBottom: `1px solid ${dividerColor}`,
    display: 'flex',
    flexDirection: 'column',
    marginTop: theme.spacing(2),
    paddingBottom: theme.spacing(1),

    '&:last-child': {
      borderBottom: 'none',
      paddingBottom: 0,
    },
  },
  logText: {
    color: '#666',
    fontSize: 16,
  },
  logItemLine: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
  },
  detailsContainer: {
    marginTop: theme.spacing(2),

    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
}));

interface AlarmViewProps {
  alarmDetails: AlarmDetails;
  siteId: number;
}

interface AlarmLogItemProps {
  datetime: Date;
  message?: string;
  title: string;
  user?: string;
}

const AlarmView = ({ alarmDetails, siteId }: AlarmViewProps) => {
  const history = useHistory();
  const {
    acknowledgement,
    activatedAt,
    alarmId,
    alarmTag: { alarmFaultCode, alarmLearnMore, id, label },
    notes,
    relatedTags,
    resolvedAt,
    status,
  } = alarmDetails;
  const classes = useStyles({ multipleSections: !!alarmFaultCode });

  const [activeTab, setActiveTab] = useState<'activity' | 'history'>('activity');

  const handleTabChange = (tab: 'activity' | 'history') => {
    setActiveTab(tab);
  };

  const logItems: AlarmLogItemProps[] = [
    {
      title: 'Triggered',
      datetime: new Date(activatedAt),
    },
  ];
  if (resolvedAt) {
    logItems.push({
      title: 'Resolved',
      datetime: new Date(resolvedAt),
    });
  }

  notes.forEach((note) => {
    logItems.push({
      title: note.timeStamp === acknowledgement?.timeStamp ? 'Acknowledged' : 'Note',
      datetime: new Date(note.timeStamp),
      user: note.userEmail,
      message: note.contents,
    });
  });

  const renderedLogItems = orderBy(logItems, (item) => item.datetime, 'desc').map((item) => (
    <div key={item.datetime.toISOString()} className={classes.logItem}>
      <div className={classes.logItemLine}>
        <Typography>{item.title}</Typography>
        <Typography>{item.datetime.toLocaleString()}</Typography>
      </div>
      <div className={classes.logItemLine}>
        {item.message && <Typography>{item.message}</Typography>}
        {item.user && <Typography>{item.user}</Typography>}
      </div>
    </div>
  ));

  const copyToClipboard = () => {
    navigator.clipboard.writeText(alarmFaultCode || '');
  };

  return (
    <div>
      <Typography variant="h6">{label}</Typography>
      <Grid container spacing={2} className={classes.detailsContainer}>
        {alarmFaultCode && (
          <Grid item className={classes.sectionContainer}>
            <div className={classes.section}>
              <Typography className={classes.label}>ERROR CODE</Typography>
              <div
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              >
                <Typography className={classes.errorCode}>{alarmFaultCode}</Typography>
                <Button onClick={copyToClipboard}>
                  <FileCopy className={classes.copyIcon} />
                </Button>
              </div>
              {alarmLearnMore && (
                <button
                  className={classes.learnMore}
                  onClick={() => history.push(PathBuilder.ALARM_FAULT_CODE(siteId, alarmId, id))}
                >
                  Learn more
                </button>
              )}
            </div>
          </Grid>
        )}
        <Grid item className={classes.sectionContainer}>
          <div className={classes.section}>
            <Typography className={classes.label}>STATUS</Typography>
            <Typography>{status}</Typography>
            {relatedTags?.length > 0 && (
              <>
                <Typography className={classes.label} style={{ marginTop: 16 }}>
                  ASSOCIATED TAGS
                </Typography>
                {relatedTags.map((tag) => (
                  <Typography key={tag.id}>
                    {tag.label} ({tag.unit})
                  </Typography>
                ))}
              </>
            )}
            <Button
              variant="outlined"
              className={classes.analysisButton}
              to={getAnalysisUrl(siteId, alarmDetails)}
              component={Link}
            >
              VIEW HISTORICAL DATA
            </Button>
          </div>
        </Grid>
      </Grid>

      <div>
        <div className={classes.tabs}>
          <Typography
            component="span"
            onClick={() => handleTabChange('activity')}
            className={`${classes.tab} ${activeTab === 'activity' ? 'active' : ''}`}
          >
            ACTIVITY
          </Typography>
          <Typography
            component="span"
            onClick={() => handleTabChange('history')}
            className={`${classes.tab} ${activeTab === 'history' ? 'active' : ''}`}
          >
            HISTORY
          </Typography>
        </div>

        {activeTab === 'activity' ? (
          <div className={classes.activity}>{renderedLogItems}</div>
        ) : (
          <AlarmHistoryLoader alarmDetails={alarmDetails} />
        )}
      </div>
      <Route path={PathPattern.ALARM_NOTE}>
        <WithRequest
          actionCreator={addNote.request}
          onSuccess={() => {
            history.push(PathBuilder.ALARM_DETAILS(siteId, alarmId, id));
          }}
          renderSuccess={renderSuccess}
          renderError={({ error }) => renderError(error)}
          onSuccessDelay={1000}
          render={({ status: requestStatus, boundActionCreator }) => (
            <UpdateModal
              alarmId={alarmId}
              alarmTagId={id}
              handleSubmit={boundActionCreator}
              label={label}
              noteRequired
              primaryButtonLabel="Add Note"
              requestStatus={requestStatus}
              siteId={siteId}
              status={status}
              title="Add Note"
              triggeredDate={new Date(activatedAt).toLocaleString()}
            />
          )}
        />
      </Route>
      <Route path={PathPattern.ALARM_ACK}>
        <WithRequest
          actionCreator={acknowledgeAlarm.request}
          onSuccess={() => {
            history.push(PathBuilder.ALARM_DETAILS(siteId, alarmId, id));
          }}
          renderSuccess={renderSuccess}
          renderError={({ error }) => renderError(error)}
          onSuccessDelay={1000}
          render={({ status: requestStatus, boundActionCreator }) => (
            <UpdateModal
              alarmId={alarmId}
              alarmTagId={id}
              handleSubmit={boundActionCreator}
              label={label}
              noteRequired={alarmDetails.alarmTag.acknowledgementNoteRequired}
              primaryButtonLabel="Acknowledge"
              requestStatus={requestStatus}
              siteId={siteId}
              status={status}
              title="Acknowledge Alarm"
              triggeredDate={new Date(activatedAt).toLocaleString()}
            />
          )}
        />
      </Route>
      <Route path={PathPattern.ALARM_RESOLVE}>
        <WithRequest
          actionCreator={resolveAlarm.request}
          onSuccess={() => {
            history.push(PathBuilder.ALARM_DETAILS(siteId, alarmId, id));
          }}
          renderSuccess={renderSuccess}
          renderError={({ error }) => renderError(error)}
          onSuccessDelay={1000}
          render={({ status: requestStatus, boundActionCreator }) => (
            <AlarmResolveModal
              triggeredDate={new Date(activatedAt).toLocaleString()}
              siteId={siteId}
              handleSubmit={boundActionCreator}
              requestStatus={requestStatus}
              status={status}
              alarmDetails={alarmDetails}
            />
          )}
        />
      </Route>
      <Route path={PathPattern.ALARM_FAULT_CODE}>
        <FaultModal
          alarmId={alarmId}
          id={id}
          siteId={siteId}
          alarmFaultCode={alarmFaultCode}
          alarmLearnMore={alarmLearnMore}
        />
      </Route>
    </div>
  );
};

export default AlarmView;
